import { ViewPreset, ViewPresetConfig } from '@bryntum/schedulerpro';
import { getISOWeek } from 'date-fns';
import { Composer } from 'vue-i18n';

import { ScheduleConstants } from '@/common/bryntum/constants';

const enum HeaderRowCellType {
  YEAR = 'header-cell-year',
  MONTH = 'header-cell-month',
  CW = 'header-cell-cw',
  DAY = 'header-cell-day',
  DAY_HOURLY = 'header-cell-day-hourly',
  HOUR = 'header-cell-hour',
}

export const enum SchedulerViewPreset {
  YEAR_MONTH = 'yearMonth',
  YEAR_MONTH_2 = 'yearMonth2',
  YEAR_MONTH_3 = 'yearMonth3',
  MONTH_WEEK = 'monthWeek',
  MONTH_WEEK_2 = 'monthWeek2',
  MONTH_WEEK_3 = 'monthWeek3',
  MONTH_WEEK_DAY = 'monthWeekDay',
  MONTH_WEEK_DAY_2 = 'monthWeekDay2',
  MONTH_WEEK_DAY_3 = 'monthWeekDay3',
  MONTH_WEEK_DAY_4 = 'monthWeekDay4',
  DAY_HOUR = 'dayHour',
  DAY_HOUR_2 = 'dayHour2',
}

/**
 * Specifies if the planning of events in the scheduler is in day resolution for the given preset.
 * @param preset
 * @returns
 */
export function isDayResolution(preset: ViewPresetConfig | ViewPreset | undefined): boolean {
  return preset?.timeResolution?.unit === 'day';
}
/**
 * Specifies if the planning of events in the scheduler is in hour resolution for the given preset.
 * @param preset
 * @returns
 */
export function isMinuteResolution(preset: ViewPresetConfig | ViewPreset | undefined): boolean {
  return preset?.timeResolution?.unit === 'minute';
}

/**
 * Bryntums displayed granularity is determined by the lowest header
 */
function getDisplayedGranularity(preset: ViewPresetConfig | ViewPreset | undefined) {
  const headerLength = preset?.headers?.length;
  if (!headerLength) return undefined;

  return preset.headers?.[headerLength - 1]?.unit;
}

/**
 * Specifies if the lowest level of the given preset is in day granularity.
 * @param preset
 * @returns
 */
export function isWeekGranularity(preset: ViewPresetConfig | ViewPreset | undefined): boolean {
  return getDisplayedGranularity(preset) === 'week';
}

/**
 * Specifies if the lowest level of the given preset is in day granularity.
 * @param preset
 * @returns
 */
export function isDayGranularity(preset: ViewPresetConfig | ViewPreset | undefined): boolean {
  const headerLength = preset?.headers?.length;
  if (!headerLength) return false;
  return preset.headers?.[headerLength - 1]?.unit === 'day';
}

/**
 * Specifies if the lowest level of the given preset is in hour granularity.
 * @param preset
 * @returns
 */
export function isHourGranularity(preset: ViewPresetConfig | ViewPreset | undefined): boolean {
  const headerLength = preset?.headers?.length;
  if (!headerLength) return false;
  return preset.headers?.[headerLength - 1]?.unit === 'hour';
}

export function getDefaultViewPreset(): SchedulerViewPreset {
  return SchedulerViewPreset.MONTH_WEEK_DAY_2;
}

/**
 * Renderers for the headers of the presets.
 */
const useHeaderRenderers = (i18n: Composer) => {
  const renderYear = () => (date: Date) => {
    return date.getFullYear();
  };
  const renderMonthNumeric = () => (date: Date) => {
    return i18n.d(new Date(date), 'monthNumeric');
  };
  const renderMonth =
    ({ short, includeYear }: { short?: boolean; includeYear?: boolean } = {}) =>
    (date: Date) => {
      let format = '';
      if (short && includeYear) {
        format = 'monthShortYear';
      } else if (short && !includeYear) {
        format = 'monthShort';
      } else if (!short && includeYear) {
        format = 'monthLongYear';
      } else if (!short && !includeYear) {
        format = 'monthLong';
      }
      return i18n.d(new Date(date), format);
    };
  const renderWeek =
    ({ short }: { short?: boolean } = {}) =>
    (date: Date) => {
      return short ? getISOWeek(date) : i18n.t('Calendar.calendarWeek', getISOWeek(date));
    };
  const renderDay =
    ({ short }: { short?: boolean } = {}) =>
    (date: Date) => {
      return short
        ? i18n.d(new Date(date), 'dateDMYShortW')
        : `
            <p class="tw-ds-text-xs tw-text-secondary-medium">
                ${i18n.d(new Date(date), 'weekDay')}
            </p>
            <p>${date.getDate()}</p>
`;
    };
  const renderHour =
    ({ short }: { short?: boolean } = {}) =>
    (date: Date) => {
      return short ? date.getHours().toString() : `${date.getHours()}:00`;
    };

  return {
    renderYear,
    renderMonthNumeric,
    renderMonth,
    renderWeek,
    renderDay,
    renderHour,
  };
};

export const SchedulerViewPresetTickWidths: Record<SchedulerViewPreset, number> = {
  [SchedulerViewPreset.YEAR_MONTH]: 40,
  [SchedulerViewPreset.YEAR_MONTH_2]: 60,
  [SchedulerViewPreset.YEAR_MONTH_3]: 110,
  [SchedulerViewPreset.MONTH_WEEK]: 30,
  [SchedulerViewPreset.MONTH_WEEK_2]: 60,
  [SchedulerViewPreset.MONTH_WEEK_3]: 100,
  [SchedulerViewPreset.MONTH_WEEK_DAY]: 30,
  [SchedulerViewPreset.MONTH_WEEK_DAY_2]: 40,
  [SchedulerViewPreset.MONTH_WEEK_DAY_3]: 60,
  [SchedulerViewPreset.MONTH_WEEK_DAY_4]: 120,
  [SchedulerViewPreset.DAY_HOUR]: 30,
  [SchedulerViewPreset.DAY_HOUR_2]: 70,
};

/**
 * Returns custom view presets for the different zoom levels of the main scheduler.
 * @returns
 */
export function useSchedulerViewPresets(
  i18n: Composer,
  options: { showHourlyPresets?: boolean; showScheduleAlertHeader?: boolean } = {},
): ViewPresetConfig[] {
  const headerRenderers = useHeaderRenderers(i18n);

  const yearMonth: ViewPresetConfig = {
    id: SchedulerViewPreset.YEAR_MONTH,
    name: SchedulerViewPreset.YEAR_MONTH,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.YEAR_MONTH],
    tickHeight: 50,
    displayDateFormat: 'll',
    shiftUnit: 'year',
    shiftIncrement: 1,
    defaultSpan: 10,
    timeResolution: {
      unit: 'month',
      increment: 1,
    },
    headers: [
      {
        unit: 'year',
        renderer: headerRenderers.renderYear(),
        headerCellCls: HeaderRowCellType.YEAR,
      },
      {
        unit: 'month',
        renderer: headerRenderers.renderMonthNumeric(),
        headerCellCls: HeaderRowCellType.MONTH,
      },
    ],
    columnLinesFor: 1,
  };

  const yearMonth2: ViewPresetConfig = {
    id: SchedulerViewPreset.YEAR_MONTH_2,
    name: SchedulerViewPreset.YEAR_MONTH_2,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.YEAR_MONTH_2],
    tickHeight: 110,
    displayDateFormat: 'll',
    shiftUnit: 'month',
    shiftIncrement: 3,
    defaultSpan: 12,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'year',
        renderer: headerRenderers.renderYear(),
        headerCellCls: HeaderRowCellType.YEAR,
      },
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth({ short: true }),
        headerCellCls: HeaderRowCellType.MONTH,
      },
    ],
    columnLinesFor: 1,
  };

  const yearMonth3: ViewPresetConfig = {
    id: SchedulerViewPreset.YEAR_MONTH_3,
    name: SchedulerViewPreset.YEAR_MONTH_3,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.YEAR_MONTH_3],
    tickHeight: 110,
    displayDateFormat: 'll',
    shiftUnit: 'month',
    shiftIncrement: 3,
    defaultSpan: 12,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'year',
        renderer: headerRenderers.renderYear(),
        headerCellCls: HeaderRowCellType.YEAR,
      },
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth(),
        headerCellCls: HeaderRowCellType.MONTH,
      },
    ],
    columnLinesFor: 1,
  };

  const monthWeek: ViewPresetConfig = {
    id: SchedulerViewPreset.MONTH_WEEK,
    name: SchedulerViewPreset.MONTH_WEEK,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.MONTH_WEEK],
    tickHeight: 40,
    displayDateFormat: 'll',
    shiftUnit: 'week',
    shiftIncrement: 1,
    defaultSpan: 10,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth({ short: true, includeYear: true }),
        headerCellCls: HeaderRowCellType.MONTH,
      },
      {
        unit: 'week',
        renderer: headerRenderers.renderWeek({ short: true }),
        headerCellCls: HeaderRowCellType.CW,
      },
    ],
    mainHeaderLevel: 1,
    columnLinesFor: 1,
  };

  const monthWeek2: ViewPresetConfig = {
    id: SchedulerViewPreset.MONTH_WEEK_2,
    name: SchedulerViewPreset.MONTH_WEEK_2,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.MONTH_WEEK_2],
    tickHeight: 40,
    displayDateFormat: 'll',
    shiftUnit: 'week',
    shiftIncrement: 1,
    defaultSpan: 10,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth({ includeYear: true }),
        headerCellCls: HeaderRowCellType.MONTH,
      },
      {
        unit: 'week',
        renderer: headerRenderers.renderWeek({ short: true }),
        headerCellCls: HeaderRowCellType.CW,
      },
    ],
    mainHeaderLevel: 1,
    columnLinesFor: 1,
  };

  const monthWeek3: ViewPresetConfig = {
    id: SchedulerViewPreset.MONTH_WEEK_3,
    name: SchedulerViewPreset.MONTH_WEEK_3,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.MONTH_WEEK_3],
    tickHeight: 105,
    displayDateFormat: 'll',
    shiftUnit: 'week',
    shiftIncrement: 5,
    defaultSpan: 6,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth({ includeYear: true }),
        headerCellCls: HeaderRowCellType.MONTH,
      },
      {
        unit: 'week',
        renderer: headerRenderers.renderWeek(),
        headerCellCls: HeaderRowCellType.CW,
      },
    ],
    mainHeaderLevel: 1,
    columnLinesFor: 1,
  };

  const monthWeekDay: ViewPresetConfig = {
    id: SchedulerViewPreset.MONTH_WEEK_DAY,
    name: SchedulerViewPreset.MONTH_WEEK_DAY,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.MONTH_WEEK_DAY],
    tickHeight: 50,
    displayDateFormat: 'll',
    shiftUnit: 'week',
    shiftIncrement: 1,
    defaultSpan: 10,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth({ includeYear: true }),
        headerCellCls: HeaderRowCellType.MONTH,
      },
      {
        unit: 'week',
        renderer: headerRenderers.renderWeek(),
        headerCellCls: HeaderRowCellType.CW,
      },
      {
        unit: 'day',
        renderer: headerRenderers.renderDay(),
        headerCellCls: HeaderRowCellType.DAY,
      },
    ],
    mainHeaderLevel: 2,
    columnLinesFor: 2,
  };

  const monthWeekDay2: ViewPresetConfig = {
    id: SchedulerViewPreset.MONTH_WEEK_DAY_2,
    name: SchedulerViewPreset.MONTH_WEEK_DAY_2,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.MONTH_WEEK_DAY_2],
    tickHeight: 50,
    displayDateFormat: 'll',
    shiftUnit: 'week',
    shiftIncrement: 1,
    defaultSpan: 10,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth({ includeYear: true }),
        headerCellCls: HeaderRowCellType.MONTH,
      },
      {
        unit: 'week',
        renderer: headerRenderers.renderWeek(),
        headerCellCls: HeaderRowCellType.CW,
      },
      {
        unit: 'day',
        renderer: headerRenderers.renderDay(),
        headerCellCls: HeaderRowCellType.DAY,
      },
    ],
    mainHeaderLevel: 2,
    columnLinesFor: 2,
  };

  const monthWeekDay3: ViewPresetConfig = {
    id: SchedulerViewPreset.MONTH_WEEK_DAY_3,
    name: SchedulerViewPreset.MONTH_WEEK_DAY_3,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.MONTH_WEEK_DAY_3],
    tickHeight: 80,
    displayDateFormat: 'll LST',
    shiftUnit: 'week',
    shiftIncrement: 1,
    defaultSpan: 1,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth({ includeYear: true }),
        headerCellCls: HeaderRowCellType.MONTH,
      },
      {
        unit: 'week',
        renderer: headerRenderers.renderWeek(),
        headerCellCls: HeaderRowCellType.CW,
      },
      {
        unit: 'day',
        renderer: headerRenderers.renderDay(),
        headerCellCls: HeaderRowCellType.DAY,
      },
    ],
    mainHeaderLevel: 2,
    columnLinesFor: 2,
  };

  const monthWeekDay4: ViewPresetConfig = {
    id: SchedulerViewPreset.MONTH_WEEK_DAY_4,
    name: SchedulerViewPreset.MONTH_WEEK_DAY_4,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.MONTH_WEEK_DAY_4],
    tickHeight: 80,
    displayDateFormat: 'll LST',
    shiftUnit: 'week',
    shiftIncrement: 1,
    defaultSpan: 1,
    timeResolution: {
      unit: 'day',
      increment: 1,
    },
    headers: [
      {
        unit: 'month',
        renderer: headerRenderers.renderMonth({ includeYear: true }),
        headerCellCls: HeaderRowCellType.MONTH,
      },
      {
        unit: 'week',
        renderer: headerRenderers.renderWeek(),
        headerCellCls: HeaderRowCellType.CW,
      },
      {
        unit: 'day',
        renderer: headerRenderers.renderDay(),
        headerCellCls: HeaderRowCellType.DAY,
      },
    ],
    mainHeaderLevel: 2,
    columnLinesFor: 2,
  };

  const dayHour: ViewPresetConfig = {
    id: SchedulerViewPreset.DAY_HOUR,
    name: SchedulerViewPreset.DAY_HOUR,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.DAY_HOUR],
    tickHeight: 40,
    displayDateFormat: 'll LST',
    shiftUnit: 'day',
    shiftIncrement: 1,
    defaultSpan: 24,
    timeResolution: {
      unit: 'minute',
      increment: ScheduleConstants.MINUTE_SNAP,
    },
    headers: [
      {
        unit: 'day',
        renderer: headerRenderers.renderDay({ short: true }),
        headerCellCls: HeaderRowCellType.DAY_HOURLY,
      },
      {
        unit: 'hour',
        renderer: headerRenderers.renderHour({ short: true }),
        headerCellCls: HeaderRowCellType.HOUR,
      },
    ],
    mainHeaderLevel: 1,
    columnLinesFor: 1,
  };

  const dayHour2: ViewPresetConfig = {
    id: SchedulerViewPreset.DAY_HOUR_2,
    name: SchedulerViewPreset.DAY_HOUR_2,
    tickWidth: SchedulerViewPresetTickWidths[SchedulerViewPreset.DAY_HOUR_2],
    tickHeight: 40,
    displayDateFormat: 'll LST',
    shiftUnit: 'day',
    shiftIncrement: 1,
    defaultSpan: 24,
    timeResolution: {
      unit: 'minute',
      increment: ScheduleConstants.MINUTE_SNAP,
    },
    headers: [
      {
        unit: 'day',
        renderer: headerRenderers.renderDay({ short: true }),
        headerCellCls: HeaderRowCellType.DAY_HOURLY,
      },
      {
        unit: 'hour',
        renderer: headerRenderers.renderHour(),
        headerCellCls: HeaderRowCellType.HOUR,
      },
    ],
    mainHeaderLevel: 1,
    columnLinesFor: 1,
  };

  const presets = [
    yearMonth,
    yearMonth2,
    yearMonth3,
    monthWeek,
    monthWeek2,
    monthWeek3,
    monthWeekDay,
    monthWeekDay2,
    monthWeekDay3,
    monthWeekDay4,
    dayHour,
    dayHour2,
  ].filter((preset) => {
    return options.showHourlyPresets || !isMinuteResolution(preset);
  });

  return presets.map((preset) => ({
    ...preset,
    headers: [
      ...preset.headers!,
      {
        ...preset.headers![preset.headers!.length - 1],
        renderer: () => '',
        headerCellCls: 'schedule-alert-header',
      },
    ],
  }));
}
